import { useEffect, useState } from "react";
import { X, Sparkles, Loader2, RotateCcw, Check, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../../../shared/components/ui";
import { orcamentoService } from "../service/orcamento.service";
import { useParams } from "react-router-dom";

interface AiAnaliseOrcamentoModalProps {
  open: boolean;
  onClose: () => void;
  methods: any;
  budgetId?: string | null;
}

interface AiOrcamentoAnalise {
  correcoes: Array<{
    stepIndex: number;
    description: string;
    itens: Array<{
      itemIndex: number;
      description: string;
    }>;
  }>;
  margemSugerida: {
    idProfitMargin?: string;
    percentual: number;
    justificativa: string;
  };
  custoExtraRisco: {
    percentual: number;
    justificativa: string;
  };
  escopo: string;
}

// Converte o texto de escopo gerado pela IA (linhas soltas e itens prefixados com "- ")
// em HTML semântico compatível com o editor WYSIWYG do campo Observações.
function escopoParaHtml(texto: string): string {
  const linhas = texto
    .split(/\r?\n/)
    .map((linha) => linha.trim())
    .filter(Boolean);

  const partesHtml: string[] = [];
  let dentroDeLista = false;

  linhas.forEach((linha) => {
    if (linha.startsWith("- ")) {
      if (!dentroDeLista) {
        partesHtml.push("<ul>");
        dentroDeLista = true;
      }
      partesHtml.push(`<li>${linha.slice(2)}</li>`);
    } else {
      if (dentroDeLista) {
        partesHtml.push("</ul>");
        dentroDeLista = false;
      }
      partesHtml.push(`<p>${linha}</p>`);
    }
  });

  if (dentroDeLista) partesHtml.push("</ul>");

  return partesHtml.join("");
}

export function AiAnaliseOrcamentoModal({ open, onClose, methods, budgetId }: AiAnaliseOrcamentoModalProps) {
  const { id: routeId } = useParams<{ id: string }>();
  const actualBudgetId = budgetId || routeId;
  const [contexto, setContexto] = useState("");
  const [analiseIniciada, setAnaliseIniciada] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<AiOrcamentoAnalise | null>(null);
  const [margensDisponiveis, setMargensDisponiveis] = useState<any[]>([]);
  const [correcoesAplicadas, setCorrecoesAplicadas] = useState(false);
  const [margemAplicada, setMargemAplicada] = useState(false);
  const [custoExtraAplicado, setCustoExtraAplicado] = useState(false);
  const [escopoAplicado, setEscopoAplicado] = useState(false);

  const executarAnalise = async () => {
    if (!actualBudgetId) {
      setError("É necessário salvar o orçamento antes de analisá-lo com IA.");
      return;
    }

    setAnaliseIniciada(true);
    setLoading(true);
    setError(null);
    setCorrecoesAplicadas(false);
    setMargemAplicada(false);
    setCustoExtraAplicado(false);
    setEscopoAplicado(false);

    try {
      const margensStorage = localStorage.getItem("profitMarginBudgetConfigs");
      const margens = margensStorage ? JSON.parse(margensStorage) : [];
      setMargensDisponiveis(margens);

      const analise = await orcamentoService.analisarComIA(
        actualBudgetId,
        contexto.trim() || undefined
      );
      setResultado(analise);
    } catch (err: any) {
      setError(err?.message || "Erro ao analisar o orçamento com IA.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setContexto("");
      setAnaliseIniciada(false);
      setResultado(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const aplicarCorrecoes = () => {
    if (!resultado) return;
    resultado.correcoes.forEach((stepCorr) => {
      methods.setValue(`steps.${stepCorr.stepIndex}.description`, stepCorr.description, {
        shouldDirty: true,
        shouldValidate: true,
      });
      stepCorr.itens.forEach((itemCorr) => {
        methods.setValue(
          `steps.${stepCorr.stepIndex}.itens.${itemCorr.itemIndex}.description`,
          itemCorr.description,
          { shouldDirty: true, shouldValidate: true }
        );
      });
    });
    setCorrecoesAplicadas(true);
    toast.success("Correções aplicadas ao orçamento.");
  };

  const margemConfigSugerida = resultado?.margemSugerida?.idProfitMargin
    ? margensDisponiveis.find((m) => m.id === resultado.margemSugerida.idProfitMargin)
    : undefined;

  const aplicarMargem = () => {
    if (!resultado) return;
    if (margemConfigSugerida) {
      methods.setValue("isProfitMargin", true, { shouldDirty: true });
      methods.setValue("idProfitMargin", margemConfigSugerida.id, { shouldDirty: true, shouldValidate: true });
      setMargemAplicada(true);
      toast.success("Margem de lucro aplicada ao orçamento.");
    }
  };

  const aplicarEscopo = () => {
    if (!resultado) return;
    methods.setValue("observation", escopoParaHtml(resultado.escopo), { shouldDirty: true, shouldValidate: true });
    setEscopoAplicado(true);
    toast.success("Escopo aplicado ao campo Observações.");
  };

  const aplicarCustoExtra = () => {
    if (!resultado || typeof resultado.custoExtraRisco.percentual !== "number") return;
    methods.setValue("percentageExtraCost", resultado.custoExtraRisco.percentual, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setCustoExtraAplicado(true);
    toast.success("Custo extra de risco aplicado ao orçamento.");
  };

  const refazerAnalise = () => {
    setAnaliseIniciada(false);
    setResultado(null);
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-slate-800">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Análise do Orçamento por IA
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!actualBudgetId && !analiseIniciada && (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 text-center dark:border-orange-900 dark:bg-orange-900/20">
              <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              <p className="text-sm text-orange-700 dark:text-orange-200">
                É necessário <strong>salvar o orçamento</strong> antes de analisá-lo com IA.
              </p>
            </div>
          )}
          {actualBudgetId && !analiseIniciada && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Descreva (opcional) a complexidade do ambiente ou situações particulares da obra — acesso difícil,
                necessidade de proteção/isolamento, prazo apertado, imprevistos climáticos, retrabalho, cliente
                exigente etc. Isso ajuda a IA a sugerir uma margem de lucro e um custo extra de risco mais precisos.
              </p>
              <textarea
                value={contexto}
                onChange={(e) => setContexto(e.target.value)}
                rows={5}
                placeholder="Ex.: Obra em área residencial de acesso restrito, necessidade de proteção da vizinhança, previsão de chuvas na região no período de execução..."
                className="w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-700 focus:border-primary-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
              />
              <Button
                type="button"
                variant="primary"
                size="sm"
                startIcon={<Sparkles className="h-4 w-4" />}
                onClick={executarAnalise}
                className="self-start"
              >
                Analisar com IA
              </Button>
            </div>
          )}

          {analiseIniciada && loading && (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-slate-500 dark:text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
              <span className="text-sm">Analisando orçamento com IA...</span>
            </div>
          )}

          {analiseIniciada && !loading && error && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <Button variant="outline" size="sm" startIcon={<RotateCcw className="h-4 w-4" />} onClick={executarAnalise}>
                Tentar novamente
              </Button>
            </div>
          )}

          {analiseIniciada && !loading && !error && resultado && (
            <div className="flex flex-col gap-6">
              {/* Correções ortográficas */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Correções ortográficas
                </h3>
                {resultado.correcoes.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Nenhuma correção necessária — as descrições já estão corretas.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {resultado.correcoes.map((stepCorr) => (
                      <div
                        key={stepCorr.stepIndex}
                        className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-900/40"
                      >
                        <p className="font-medium text-slate-700 dark:text-slate-200">
                          Etapa {stepCorr.stepIndex + 1}: <span className="font-normal">{stepCorr.description}</span>
                        </p>
                        {stepCorr.itens.map((itemCorr) => (
                          <p key={itemCorr.itemIndex} className="ml-4 mt-1 text-slate-600 dark:text-slate-300">
                            Item {itemCorr.itemIndex + 1}: {itemCorr.description}
                          </p>
                        ))}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant={correcoesAplicadas ? "secondary" : "primary"}
                      size="sm"
                      startIcon={correcoesAplicadas ? <Check className="h-4 w-4" /> : undefined}
                      onClick={aplicarCorrecoes}
                      disabled={correcoesAplicadas}
                      className="self-start"
                    >
                      {correcoesAplicadas ? "Correções aplicadas" : "Aplicar todas as correções"}
                    </Button>
                  </div>
                )}
              </section>

              {/* Margem de lucro sugerida */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Margem de lucro sugerida
                </h3>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-900/40">
                  {margemConfigSugerida ? (
                    <p className="font-medium text-slate-700 dark:text-slate-200">
                      {margemConfigSugerida.name} — {margemConfigSugerida.value}%
                    </p>
                  ) : resultado.margemSugerida.percentual ? (
                    <p className="font-medium text-slate-700 dark:text-slate-200">
                      Sugestão: {resultado.margemSugerida.percentual}% (nenhuma margem cadastrada corresponde a esse
                      valor — cadastre em Configurações Gerais para poder aplicá-la aqui)
                    </p>
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400">Não foi possível sugerir uma margem.</p>
                  )}
                  {resultado.margemSugerida.justificativa && (
                    <p className="mt-1 text-slate-500 dark:text-slate-400">
                      {resultado.margemSugerida.justificativa}
                    </p>
                  )}
                  {margemConfigSugerida && (
                    <Button
                      type="button"
                      variant={margemAplicada ? "secondary" : "primary"}
                      size="sm"
                      startIcon={margemAplicada ? <Check className="h-4 w-4" /> : undefined}
                      onClick={aplicarMargem}
                      disabled={margemAplicada}
                      className="mt-3"
                    >
                      {margemAplicada ? "Margem aplicada" : "Aplicar margem sugerida"}
                    </Button>
                  )}
                </div>
              </section>

              {/* Custo extra de risco sugerido */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Custo extra de risco sugerido
                </h3>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-900/40">
                  {typeof resultado.custoExtraRisco.percentual === "number" ? (
                    <p className="font-medium text-slate-700 dark:text-slate-200">
                      {resultado.custoExtraRisco.percentual}%
                    </p>
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400">Nenhum custo extra de risco sugerido.</p>
                  )}
                  {resultado.custoExtraRisco.justificativa && (
                    <p className="mt-1 text-slate-500 dark:text-slate-400">{resultado.custoExtraRisco.justificativa}</p>
                  )}
                  {typeof resultado.custoExtraRisco.percentual === "number" && resultado.custoExtraRisco.percentual > 0 && (
                    <Button
                      type="button"
                      variant={custoExtraAplicado ? "secondary" : "primary"}
                      size="sm"
                      startIcon={custoExtraAplicado ? <Check className="h-4 w-4" /> : undefined}
                      onClick={aplicarCustoExtra}
                      disabled={custoExtraAplicado}
                      className="mt-3"
                    >
                      {custoExtraAplicado ? "Custo extra aplicado" : "Aplicar custo extra sugerido"}
                    </Button>
                  )}
                </div>
              </section>

              {/* Escopo gerado */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Escopo gerado</h3>
                <textarea
                  readOnly
                  value={resultado.escopo}
                  rows={6}
                  className="w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-700 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
                />
                <Button
                  type="button"
                  variant={escopoAplicado ? "secondary" : "primary"}
                  size="sm"
                  startIcon={escopoAplicado ? <Check className="h-4 w-4" /> : undefined}
                  onClick={aplicarEscopo}
                  disabled={escopoAplicado}
                  className="mt-2"
                >
                  {escopoAplicado ? "Escopo aplicado" : "Aplicar em Observações"}
                </Button>
              </section>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 dark:border-slate-700">
          {analiseIniciada && !loading && resultado ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              startIcon={<RotateCcw className="h-4 w-4" />}
              onClick={refazerAnalise}
            >
              Refazer análise
            </Button>
          ) : (
            <span />
          )}
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
}
